// src/pos-app/surfaces/waiter/SalesPinModal.js
//
// F12a-N Task W5 — the "Sales" cashier-switch PIN modal (design contract:
// docs/mockups/f12a-n/waiter-tables.html `#pinModal`/`.pin-modal`, opened by
// the top-bar `#btnSales` button). Parity source: inventory Surface A
// "Cashier PIN switch" — TableServicePage.js:146-173 (handlePinSubmit),
// 792-800 (Sales button), 1789-1814 (Modal render).
//
// Validation is a byte-faithful port of the legacy gate:
//   POST /auth/employee-login {tenant_id, pin} → require
//   is_cashier===true OR user_role in {admin,manager}, else deny.
//
// Deliberately DIFFERENT from legacy past that point — this is the W5 task's
// documented v1 product decision, not an oversight:
//   - Legacy calls `authService.employeeLogin` (persists token/role/name to
//     localStorage unconditionally), then `flushSync`s a full AuthContext
//     identity swap to the PIN-enterer and navigates to the Cashier route.
//   - This modal instead posts DIRECTLY on the shared axios instance
//     (`services/api.js`'s default export) and only ever INSPECTS the
//     response — same "validate and discard" discipline
//     StationSettingsSheet.js's manager gate already uses for exactly this
//     reason (see that file's header). Nothing is written to localStorage
//     or AuthContext; the device's signed-in waiter operator identity is
//     left completely untouched.
//   - On success the modal calls `onSuccess()` with NO payload — the caller
//     (WaiterStation) flips a purely in-memory `salesMode` flag that swaps
//     which surface renders in the Tables pane. The station config, the
//     signed-in operator, and every order attribution stay exactly as they
//     were; this is a UI-mode toggle, not an authentication event. Product
//     decision, documented, easily changed later (e.g. if a future revision
//     wants orders placed in Sales-mode attributed to the PIN-enterer
//     instead of the waiter operator, that identity swap can be added here).
//
// No own Escape listener (F12a-N W5 Task 2 — Escape-chain centralization):
// this modal is fully controlled by `open`/`onClose` props; the single
// keydown authority lives in TablesSurface, which is passed this modal's
// open state (via WaiterStation) purely to rank/close it — see that file's
// header for the full priority chain. Digit entry is tap-only (mockup shows
// no physical-keyboard affordance for this modal either) — kept in scope
// with the task brief, which only asks for Escape centralization.
//
// Keypad UI/CSS is a close visual port of StationSettingsSheet's PIN-pad
// (digits 1-9, backspace, 0, enter/"go") per the task's explicit instruction
// to reuse that pattern, rather than the static mockup's Clear+Del layout.
//
// W5 REVIEW FIX (CRITICAL — not portaled) — this modal used to render inline
// wherever WaiterStation mounts it (a sibling of the station frame, still
// nested inside <AuroraBackground>). Same trap OperatorLockScreen.js's header
// documents at length: `.aur-root` is `position:fixed` with no explicit
// z-index, so EVERYTHING inside it — including this modal's own
// `z-index:60` — only ever competed inside that one trapped stacking
// context. Meanwhile OrderDrawer.js (see its z-index-band comment) portals
// straight to `document.body`, i.e. a DIFFERENT, real, top-level stacking
// context. Opening the Sales PIN modal over an in-progress order drawer
// therefore painted the drawer (real body-level z:55/56) ABOVE this modal
// (trapped-context z:60 that never actually competed against it) — the PIN
// pad was there, hittable in the accessibility tree, but visually gone.
// Fixed the same way OperatorLockScreen/OrderDrawer/TopBarExtras' More menu
// were: portal to `document.body`, keeping the existing `z-index:60` — which
// now genuinely lands in-band: OrderDrawer 55/56 < this 60 < OperatorLockScreen 70.
//
// Belt-and-braces (same review finding): TablesSurface additionally closes
// any in-progress order drawer the moment this modal's `open` prop flips
// true (see TablesSurface.js's effect next to its `locked`-closes-drawer
// one) — a mid-entry order should not linger, even invisibly-correctly,
// underneath a cashier-PIN prompt. Chosen over disabling the Sales button
// while a drawer is open: closing is one line, matches the existing
// `locked` precedent exactly, and never blocks the Sales action itself.
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../../../services/api';
import { useLanguage } from '../../../context/LanguageContext';

const PIN_LENGTH = 6;

const CSS = `
.wpm-overlay{position:fixed;inset:0;z-index:60;display:flex;align-items:center;justify-content:center;box-sizing:border-box;
  padding:calc(20px + var(--safe-top)) 20px calc(20px + var(--safe-bottom));font-family:inherit;}
.wpm-scrim{position:absolute;inset:0;background:rgba(2,8,23,.45);
  -webkit-backdrop-filter:blur(10px);backdrop-filter:blur(10px);}
.wpm-panel{position:relative;width:340px;max-width:100%;max-height:100%;overflow-y:auto;box-sizing:border-box;
  border-radius:20px;padding:22px;background:var(--c-surface);
  background:color-mix(in srgb, var(--c-surface) 84%, transparent);
  -webkit-backdrop-filter:blur(22px) saturate(1.5);backdrop-filter:blur(22px) saturate(1.5);
  border:1px solid var(--c-border);border-color:color-mix(in srgb, var(--c-border) 55%, transparent);
  box-shadow:0 18px 50px rgba(2,8,23,.28);}
.wpm-head{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:4px;}
.wpm-head h3{font-size:16px;font-weight:800;letter-spacing:-.2px;color:var(--c-ink);margin:0;flex:1;text-align:center;}
.wpm-close{width:44px;height:44px;border-radius:12px;flex:none;cursor:pointer;
  display:flex;align-items:center;justify-content:center;color:var(--c-muted);
  background:var(--c-faint);border:1px solid var(--c-border);}
.wpm-sub{font-size:12.5px;color:var(--c-muted);font-weight:600;margin:0 0 16px;line-height:1.5;text-align:center;}
.wpm-error{display:flex;gap:9px;align-items:flex-start;padding:11px 13px;border-radius:12px;margin-bottom:14px;
  background:var(--c-redLight);border:1px solid var(--c-redBorder);color:var(--c-redText);
  font-size:12.5px;font-weight:600;line-height:1.5;}
.wpm-dots{display:flex;direction:ltr;justify-content:center;gap:13px;margin:6px 0 18px;height:15px;align-items:center;}
.wpm-dot{width:13px;height:13px;border-radius:50%;border:2px solid var(--c-mutedLight);background:transparent;
  box-sizing:border-box;transition:all .15s ease;}
.wpm-dot.fill{background:var(--c-green);border-color:var(--c-green);transform:scale(1.12);}
.wpm-keypad{display:grid;direction:ltr;grid-template-columns:repeat(3,1fr);gap:10px;width:100%;}
.wpm-key{min-height:58px;border-radius:14px;cursor:pointer;touch-action:manipulation;
  background:var(--c-surface);color:var(--c-ink);border:1px solid var(--c-border);
  font-size:21px;font-weight:700;font-family:inherit;
  display:flex;align-items:center;justify-content:center;}
.wpm-key:disabled{opacity:.55;cursor:default;}
.wpm-key.fn{color:var(--c-muted);}
.wpm-key.go{background:var(--c-green);border-color:var(--c-green);color:#fff;}
`;

const Icon = {
    close: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
        </svg>
    ),
    backspace: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 4H8l-5 8 5 8h13a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1z" />
            <path d="m12 9 6 6M18 9l-6 6" />
        </svg>
    ),
    enter: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M5 12h14M13 6l6 6-6 6" />
        </svg>
    ),
    alert: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v4M12 16h.01" />
        </svg>
    ),
};

const isCashierOrManager = (data) => {
    const role = (data?.user_role || '').toLowerCase();
    return data?.is_cashier === true || role === 'admin' || role === 'manager';
};

export default function SalesPinModal({ open, onClose, onSuccess, tenantId }) {
    const { t } = useLanguage();
    const [pin, setPin] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [error, setError] = useState('');

    // Reset on every open (mirrors StationSettingsSheet's gate reset).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!open) return;
        setPin('');
        setVerifying(false);
        setError('');
    }, [open]);

    const fail = useCallback((message) => {
        setError(message);
        setPin('');
    }, []);

    // Faithful port of TableServicePage.js:146-173 (handlePinSubmit) up to
    // the pass/deny check — see the header note for the deliberate
    // divergence past that point (validate-and-discard, no session write).
    const submitPin = useCallback(async (pinValue) => {
        setVerifying(true);
        setError('');
        try {
            const res = await api.post('/auth/employee-login', {
                tenant_id: tenantId,
                pin: pinValue,
            });
            if (res?.data?.access_token && isCashierOrManager(res.data)) {
                setPin('');
                setVerifying(false);
                onSuccess();
            } else {
                setVerifying(false);
                fail(t('cashier_access_denied'));
            }
        } catch (err) {
            setVerifying(false);
            fail(t('invalid_credentials'));
        }
    }, [tenantId, t, fail, onSuccess]);

    const pressDigit = useCallback((digit) => {
        if (verifying || pin.length >= PIN_LENGTH) return;
        setError('');
        const next = pin + digit;
        setPin(next);
        if (next.length === PIN_LENGTH) submitPin(next);
    }, [verifying, pin, submitPin]);

    const pressBackspace = useCallback(() => {
        if (verifying) return;
        setError('');
        setPin((prev) => prev.slice(0, -1));
    }, [verifying]);

    const pressEnter = useCallback(() => {
        if (verifying || pin.length < PIN_LENGTH) return;
        submitPin(pin);
    }, [verifying, pin, submitPin]);

    // task-w — pointerdown, not click; see OperatorLockScreen.js's header
    // for the full mechanism writeup (iOS declines to synthesize a click
    // when the DOM mutates mid-gesture on a live-updating screen). Applied
    // here as cheap, future-proof consistency across every native PIN pad,
    // not because this modal sits on a ticking screen itself.
    const onPress = useCallback((fn) => (e) => {
        if (e && typeof e.button === 'number' && e.button !== 0) return;
        fn();
    }, []);

    if (!open) return null;

    // Portaled to document.body — see the header note above (W5 review fix).
    return createPortal(
        <div className="wpm-overlay" role="dialog" aria-modal="true" aria-label={t('switch_to_cashier')}>
            <style>{CSS}</style>
            <div className="wpm-scrim" onClick={onClose} />
            <div className="wpm-panel">
                <div className="wpm-head">
                    <h3>{t('switch_to_cashier')}</h3>
                    <button type="button" className="wpm-close" onClick={onClose} aria-label={t('close')}>
                        {Icon.close}
                    </button>
                </div>
                <p className="wpm-sub">{t('pin_required_for_cashier')}</p>

                {error ? (
                    <div className="wpm-error" role="alert">
                        {Icon.alert}
                        <span>{error}</span>
                    </div>
                ) : null}

                <div className="wpm-dots" aria-hidden="true">
                    {Array.from({ length: PIN_LENGTH }, (_, i) => (
                        <span key={i} className={`wpm-dot${i < pin.length ? ' fill' : ''}`} />
                    ))}
                </div>
                <div className="wpm-keypad">
                    {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                        <button key={d} type="button" className="wpm-key" disabled={verifying} onPointerDown={onPress(() => pressDigit(d))}>
                            {d}
                        </button>
                    ))}
                    <button type="button" className="wpm-key fn" disabled={verifying} onPointerDown={onPress(pressBackspace)} aria-label={t('delete')}>
                        {Icon.backspace}
                    </button>
                    <button type="button" className="wpm-key" disabled={verifying} onPointerDown={onPress(() => pressDigit('0'))}>
                        0
                    </button>
                    <button type="button" className="wpm-key go" disabled={verifying} onPointerDown={onPress(pressEnter)} aria-label={t('confirm')}>
                        {Icon.enter}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
